import { useMutation } from '@tanstack/react-query'

import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'
import { queryClient } from '@services/queryClient'

import { usePutTask } from './put'
interface IPutTaskParams {
  id: number
  task: IGenericTask
}

export function useUpdateTaskAndTaskList() {
  const { putTask } = usePutTask()
  const { selectedDate } = useSharedDates()
  const { getWeekRange } = useDate()

  const { weekStart } = getWeekRange(selectedDate)

  return useMutation({
    mutationFn: putTask,
    onMutate: handleOptimisticTaskListUpdate,
    onError: (_err, _newTask, context) => {
      if (!context) return

      const { previousTasks, tasksKey } = context
      queryClient.setQueryData(tasksKey, previousTasks)
    },
    onSettled: (_tasks, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.tasksKey,
      }),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['TASKS', weekStart.getTime()],
        type: 'active',
      })
    },
  })

  async function handleOptimisticTaskListUpdate({ task, id }: IPutTaskParams) {
    await queryClient.cancelQueries({
      queryKey: ['TASKS', weekStart.getTime()],
    })
    const [tasksQuery] = queryClient.getQueriesData<IGenericTask[]>({
      queryKey: ['TASKS', weekStart.getTime()],
      type: 'active',
    })

    const [tasksKey, tasksResult] = tasksQuery
    const previousTasks = tasksResult

    const updatedTasks = tasksResult?.map((oldTask) =>
      oldTask.id === id ? task : oldTask,
    )

    queryClient.setQueryData(tasksKey, () => updatedTasks)

    return {
      tasksKey,
      previousTasks,
      updatedTasks,
    }
  }
}
